import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TimelineRenderFrame, TimelineRenderEngine } from './types';
import { WebGLTimelineEngine } from './timeline-webgl-engine';

interface TimelineWebGLCanvasProps {
  frame: TimelineRenderFrame;
  className?: string;
  onReadyChange?: (ready: boolean) => void;
  onEngineChange?: (engine: TimelineRenderEngine | null) => void;
}

export const TimelineWebGLCanvas = ({
  frame,
  className,
  onReadyChange,
  onEngineChange,
}: TimelineWebGLCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<TimelineRenderEngine | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fallbackMessage = useMemo(
    () => 'WebGL2 is required',
    [],
  );

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const engine = new WebGLTimelineEngine();
    try {
      engine.init(canvas);
      engineRef.current = engine;
      onEngineChange?.(engine);
      onReadyChange?.(true);
      setError(null);
    } catch (initError) {
      engineRef.current = null;
      onEngineChange?.(null);
      onReadyChange?.(false);
      setError(initError instanceof Error ? initError.message : fallbackMessage);
      return;
    }

    return () => {
      engine.dispose();
      engineRef.current = null;
      onEngineChange?.(null);
      onReadyChange?.(false);
    };
  }, [fallbackMessage, onEngineChange, onReadyChange]);

  useEffect(() => {
    if (error) {
      return;
    }
    const engine = engineRef.current;
    if (!engine) {
      return;
    }
    engine.resize(frame.width, frame.height, frame.dpr);
    engine.render(frame);
  }, [error, frame]);

  if (error) {
    return (
      <div
        data-testid="timeline-webgl-error"
        className="absolute inset-0 flex items-center justify-center rounded-lg border"
        style={{
          borderColor: 'var(--color-error)',
          color: 'var(--color-error)',
          backgroundColor: 'var(--color-error-bg)',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid="timeline-webgl-canvas"
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
      }}
    />
  );
};
